import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Assessment, FunctionalTest } from '../type';

import { hasEmptyValues, reorder, trimObject, trimValue } from 'helpers';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { cloneDeep, debounce, isUndefined, orderBy } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultAssessment } from '../constant';

export const useAssessmentsFormController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id, institutionId } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const isAssessmentAdd = location.pathname.includes(`${PATH.ADD_ASSESSMENTS}`);

  const [selectedFunctionalTests, setSelectedFunctionalTests] = useState<Record<string, string>>(
    {},
  );

  const [functionalTestData, setFunctionalTestData] = useState<FunctionalTest[]>([]);

  const [selectedData, setSelectedData] = useState<FunctionalTest[]>([]);

  const [deleteSelected, setDeleteSelected] = useState<FunctionalTest[]>([]);

  const [assessmentDetail, setAssessmentDetail] = useState<Assessment>();

  const searchValue = useString('');

  const [page, setPage] = useState<number>(1);

  const totalPage = useNumber(0);

  const hasMore = useBoolean(false);

  const isLoading = useBoolean(false);

  useEffect(() => {
    if (page < 1) {
      return;
    }

    getListFunctionalTest();
  }, [page]);

  useEffect(() => {
    if (isAssessmentAdd) {
      return;
    }

    if (id) {
      getAssessmentDetail();
    }
  }, [id]);

  const getListFunctionalTest = async (search: string = '') => {
    const result: {
      items: FunctionalTest[];
      total: number;
    } = await apiCall({
      url: '/functional-tests',
      method: 'GET',
      data: {
        page: page.toString(),
        limit: 10,
        institutionId: isInstitutionDetail ? institutionId : userProfile?.institution?.id,
        search: search,
      },
    });

    hasMore.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ?? [];

    if (page < 1) return;

    if (page === 1) {
      setFunctionalTestData(newItems);
      totalPage.setValue(result.total);

      return;
    }

    setFunctionalTestData((prev) => [...prev, ...newItems]);
  };

  const getAssessmentDetail = async () => {
    const result: Assessment = await apiCall({
      url: `/assessments/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      setValues(result as any);
      setAssessmentDetail({ ...result, functionalTests: orderBy(result.functionalTests, 'index') });

      setSelectedData(orderBy(result.functionalTests, 'index'));
    }
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListFunctionalTest(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleAddFunctionalTestToAssessment = () => {
    const newSelectedData = functionalTestData.filter((el) =>
      Object.keys(selectedFunctionalTests).includes(el.id),
    );

    setSelectedData([...selectedData, ...newSelectedData]);
    setSelectedFunctionalTests({});
  };

  const handleDeleteFunctionalTestInAssessment = (index: number) => {
    const newSelectedData = cloneDeep(selectedData);

    setDeleteSelected([...deleteSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedData(newSelectedData);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(selectedData, result.source.index, result.destination.index);

    setSelectedData(items);
  };

  const handleDeleteAssessment = async () => {
    await apiCall({
      url: `/assessments/${id}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_ASSESSMENT);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_ASSESSMENT);
    navigationTo(-1);
  };

  const onCancel = () => {
    if (!isAssessmentAdd && id) {
      setValues(assessmentDetail as any);
      setSelectedData(assessmentDetail?.functionalTests as FunctionalTest[]);

      return;
    }

    formik.setValues(defaultAssessment);
    navigationTo(-1);
  };

  const onChangeSelectedFunctionalTests = (lesson: string) => () => {
    const newSelected = cloneDeep(selectedFunctionalTests);

    if (!isUndefined(newSelected[lesson])) {
      delete newSelected[lesson];
    } else {
      newSelected[lesson] = lesson;
    }

    setSelectedFunctionalTests(newSelected);
  };

  const formik = useFormik({
    initialValues: defaultAssessment,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const bodyRequest = {
        ...trimObject({
          name: values.name,
          institutionId: userProfile?.institution?.id,
          isClass: values.isClass,
          isOnboarding: values.isOnboarding,
          isDefault: values.isOnboarding ? values.isDefault : false,
          functionalTests: selectedData.map((el, index) => ({
            index,
            functionalTestId: el.id,
            assessmentFunctionalTestId: el.assessmentFunctionalTestId,
          })),
          removeAssessmentFunctionalTests: deleteSelected.map(
            (el) => el.assessmentFunctionalTestId,
          ),
        }),
      };

      if (isAssessmentAdd && isInstitutionDetail) {
        bodyRequest.institutionId = institutionId;
      }

      const result: Assessment = await apiCall({
        url: !isAssessmentAdd ? `/assessments/${id}` : '/assessments',
        method: !isAssessmentAdd ? 'PATCH' : 'POST',
        data: bodyRequest,
        isLoading: true,
        showError: true,
      });

      if (!result?.id) {
        return;
      }

      if (!isAssessmentAdd) {
        getAssessmentDetail();
        showToastMessage('success', SUCCESS_MESSAGE.UPDATE_ASSESSMENT);
        closeModal(MODAL_KEY.WARNING_ASSESSMENT);
        closeModal(MODAL_KEY.UPDATE_ASSESSMENT);

        return;
      }

      formik.setValues(defaultAssessment);
      setSelectedFunctionalTests({});
      showToastMessage('success', SUCCESS_MESSAGE.ADD_ASSESSMENT);
      closeModal(MODAL_KEY.WARNING_ASSESSMENT);
      closeModal(MODAL_KEY.UPDATE_ASSESSMENT);
      navigationTo(-1);
    },
  });

  const { values, setValues, handleChange, handleSubmit, isSubmitting, isValid, setFieldValue } =
    formik;

  const disabled =
    hasEmptyValues({ name: values.name }) ||
    isSubmitting ||
    !isValid ||
    (!values.isOnboarding && !values.isClass) ||
    selectedData.length === 0;

  const isAssessmentDetailEqual =
    String(assessmentDetail?.name) === values.name &&
    JSON.stringify(assessmentDetail?.functionalTests) === JSON.stringify(selectedData) &&
    assessmentDetail?.isClass === values.isClass &&
    assessmentDetail.isOnboarding === values.isOnboarding &&
    assessmentDetail.isDefault === values.isDefault;

  const disabledButton = useMemo(() => {
    return (
      isAssessmentDetailEqual ||
      hasEmptyValues({ name: values.name }) ||
      (!values.isOnboarding && !values.isClass) ||
      selectedData.length === 0
    );
  }, [values, assessmentDetail, selectedData]);

  const goBack = () => {
    navigationTo(-1);
  };

  const onChangeOnboarding = () => {
    setFieldValue('isOnboarding', !values.isOnboarding);
  };

  const onChangeClass = () => {
    setFieldValue('isClass', !values.isClass);
  };

  const onChangeDefault = () => {
    setFieldValue('isDefault', !values.isDefault);
  };

  const listFunctionalTest = useMemo(() => {
    const newSelectedData = selectedData.map((el) => el.id);

    return functionalTestData.filter((el) => !newSelectedData.includes(el.id));
  }, [selectedData, functionalTestData]);

  const handleLoadMoreFunctionalTest = () => {
    if (!hasMore.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPage((prev) => prev + 1);
    }, 1200);
  };

  return {
    assessmentId: isAssessmentAdd ? '' : id,
    assessmentDetail,
    goBack,
    values,
    handleChange,
    handleSubmit,
    functionalTestData: listFunctionalTest,
    searchValue,
    onChangeSearchValue,
    selectedData,
    handleAddFunctionalTestToAssessment,
    handleDeleteFunctionalTestInAssessment,
    handleDeleteAssessment,
    onCancel,
    disabled,
    disabledButton,
    onDragEnd,
    selectedFunctionalTests,
    onChangeSelectedFunctionalTests,
    onChangeClass,
    onChangeOnboarding,
    onChangeDefault,
    hasMore,
    handleLoadMoreFunctionalTest,
    isAssessmentDetailEqual,
  };
};
